import { type IReactionDisposer, makeAutoObservable, reaction } from 'mobx'
import { AxiosError, type AxiosResponse } from 'axios'
import dayjs from 'dayjs'
import { toastStore } from 'shared/ui'
import { DayjsFormats, logger } from 'shared/lib'
import {
  AIAssistantApi,
  IAIActionTypeEnum,
  IAILayoutEnum,
  type IParamsCreateSuggestAnswer,
  type IParamsCreateSummaryAnswer,
  type IResponseAIAnswer,
} from 'entities/AIAssistant'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import type { MessageFieldStore } from 'widgets/MessageField'

type IAiAssistantStoreProps = {
  actions: Array<IAIActionTypeEnum>
  getParamsCreateSuggestAnswer?: () => IParamsCreateSuggestAnswer
  getParamsCreateSummaryAnswer?: () => IParamsCreateSummaryAnswer
  additionalCreateSummaryAnswerAction?: () => void
  isVariantContained?: { value: boolean }
}

export class AiAssistantStore {
  private _getParamsCreateSuggestAnswer:
    | IAiAssistantStoreProps['getParamsCreateSuggestAnswer']
    | null = null
  private _getParamsCreateSummaryAnswer:
    | IAiAssistantStoreProps['getParamsCreateSummaryAnswer']
    | null = null
  private _actions: IAiAssistantStoreProps['actions']
  private _additionalCreateSummaryAnswerAction: IAiAssistantStoreProps['additionalCreateSummaryAnswerAction']
  private _isVariantContained: IAiAssistantStoreProps['isVariantContained']

  private _loadingGeneration = false
  private _loadingTryAgain = false
  private _open = false
  private _answer = ''
  private _actionType: IAIActionTypeEnum | null = null
  private _actionRequest: (() => void | Promise<AxiosResponse<IResponseAIAnswer>>) | null = null
  private _paramsCreateSuggestAnswer: IParamsCreateSuggestAnswer | null = null
  private _paramsCreateSummaryAnswer: IParamsCreateSummaryAnswer | null = null
  private _text = ''
  private _handleOnLike: ((answer: string) => void) | null = null
  private _template: string | null = null

  private _layout: IAILayoutEnum = IAILayoutEnum.Row
  private _disposeCurrentTextObj: IReactionDisposer | null = null

  constructor(
    private _messageFieldStore: MessageFieldStore,
    {
      actions,
      getParamsCreateSummaryAnswer,
      getParamsCreateSuggestAnswer,
      additionalCreateSummaryAnswerAction,
      isVariantContained,
    }: IAiAssistantStoreProps,
    private _entities: IEntitiesStore
  ) {
    makeAutoObservable(this)

    this._actions = actions
    this._getParamsCreateSummaryAnswer = getParamsCreateSummaryAnswer
    this._getParamsCreateSuggestAnswer = getParamsCreateSuggestAnswer
    this._additionalCreateSummaryAnswerAction = additionalCreateSummaryAnswerAction
    this._isVariantContained = isVariantContained

    this.reactionSelectedContentObj()
    this.reactionCurrentTextObj()
  }

  private _disposeSelectedContentObj: IReactionDisposer | null = null

  reactionSelectedContentObj = () => {
    this._disposeSelectedContentObj?.()
    this._disposeSelectedContentObj = reaction(
      () => this._messageFieldStore.selectedContent,
      (value) => {
        this._text = value
        if (!value.length && this.open) {
          this.setOpen(false)
        }
      }
    )
  }

  reactionCurrentTextObj = () => {
    this._disposeCurrentTextObj?.()
    this._disposeCurrentTextObj = reaction(
      () => this._messageFieldStore.messageInnerText,
      (value) => {
        if (this._text.length) {
          this._text = ''
        }

        if (value && this.open) {
          this.setOpen(false)
        }
      }
    )
  }

  get open() {
    return this._open
  }

  get triggerElement() {
    return this._messageFieldStore.messageFieldElement
  }

  get isEmpty() {
    return !this._text.length
  }

  get disabledSuggested() {
    return Boolean(!this._paramsCreateSuggestAnswer?.messages.length)
  }

  get disabledSummarizeConversation() {
    return Boolean(!this._paramsCreateSummaryAnswer?.messages.length)
  }

  get showSuggested() {
    return Boolean(this._actions?.find((action) => action === IAIActionTypeEnum.SuggestResponse))
  }

  get showSummarizeConversation() {
    return Boolean(
      this._actions?.find((action) => action === IAIActionTypeEnum.SummarizeConversation)
    )
  }

  get showRephrase() {
    return Boolean(this._actions?.find((action) => action === IAIActionTypeEnum.Rephrase))
  }

  get showMoreFriendly() {
    return Boolean(this._actions?.find((action) => action === IAIActionTypeEnum.MoreFriendly))
  }

  get showMoreFormal() {
    return Boolean(this._actions?.find((action) => action === IAIActionTypeEnum.MoreFormal))
  }

  get showExpand() {
    return Boolean(this._actions?.find((action) => action === IAIActionTypeEnum.Expand))
  }

  get showShorten() {
    return Boolean(this._actions?.find((action) => action === IAIActionTypeEnum.Shorten))
  }

  get isColumn() {
    return this._layout === IAILayoutEnum.Column
  }

  get isVariantContained() {
    return this._isVariantContained
  }

  get answer() {
    return this._answer
  }

  get loadingGeneration() {
    return this._loadingGeneration
  }

  get loadingTryAgain() {
    return this._loadingTryAgain
  }

  get actionType() {
    return this._actionType
  }

  reset = () => {
    this._answer = ''
    this._text = ''
    this._loadingGeneration = false
    this._loadingTryAgain = false
    this._paramsCreateSuggestAnswer = null
    this._paramsCreateSummaryAnswer = null
    this._template = null
  }

  dispose = () => {
    this._disposeSelectedContentObj?.()
    this._disposeCurrentTextObj?.()
  }

  handleTooltipOpen = () => {
    this._text = this._text || this._messageFieldStore.messageInnerText
    this._handleOnLike = this._messageFieldStore.replaceMessageText
    this._paramsCreateSuggestAnswer = this._getParamsCreateSuggestAnswer?.() || null
    this._paramsCreateSummaryAnswer = this._getParamsCreateSummaryAnswer?.() || null
    this.setOpen(true)
  }

  setOpen = (value: boolean) => {
    this._open = value
    if (!value) {
      this.reset()
      this._messageFieldStore.clearWindowSelection()
      this._messageFieldStore.setSelectedContent('')
    }
  }

  onAction = async (request: () => void | Promise<AxiosResponse<IResponseAIAnswer>>) => {
    try {
      this._loadingGeneration = true
      this._actionRequest = request

      const res = await request()

      if (!res?.data?.result_message) {
        this._answer =
          'The AI texting assistant ran into issues. Please update your text and retry.'
      } else {
        this._answer = this._template
          ? `${this._template}\n ${res.data.result_message}`
          : res.data.result_message
      }
    } catch (e) {
      if (e instanceof AxiosError) {
        toastStore.add({
          type: 'error',
          title: e.errors.getError('message'),
        })
        logger.error(e)
      }
    } finally {
      this._loadingGeneration = false
    }
  }

  onTryAgain = async () => {
    try {
      this._loadingTryAgain = true
      if (this._actionRequest) {
        const res = await this._actionRequest()
        this._answer = res?.data.result_message
          ? this._template
            ? `${this._template}\n ${res?.data?.result_message}`
            : res?.data?.result_message
          : ''
      }
    } catch (e) {
      if (e instanceof AxiosError) {
        toastStore.add({
          type: 'error',
          title: e.errors.getError('message'),
        })
        logger.error(e)
      }
      logger.error(e)
    } finally {
      this._loadingTryAgain = false
    }
  }

  onCreateSuggestAnswer = () => {
    this._actionType = IAIActionTypeEnum.SuggestResponse
    const request = () => {
      if (this._getParamsCreateSuggestAnswer) {
        return AIAssistantApi.createSuggestAnswer(this._getParamsCreateSuggestAnswer())
      }
    }

    this.onAction(request)
  }

  onCreateSummaryAnswer = () => {
    this._actionType = IAIActionTypeEnum.SummarizeConversation
    const request = () => {
      if (this._getParamsCreateSummaryAnswer) {
        return AIAssistantApi.createSummaryConversationAnswer(this._getParamsCreateSummaryAnswer())
      }
    }

    this._additionalCreateSummaryAnswerAction?.()

    this.setTemplate()
    this.onAction(request)
  }
  onRephrase = () => {
    this._actionType = IAIActionTypeEnum.Rephrase
    const request = () => AIAssistantApi.getRephrase(this._text)

    this.onAction(request)
  }
  onMoreFriendly = () => {
    this._actionType = IAIActionTypeEnum.MoreFriendly
    const request = () => AIAssistantApi.getMoreFriendly(this._text)

    this.onAction(request)
  }
  onMoreFormal = () => {
    this._actionType = IAIActionTypeEnum.MoreFormal
    const request = () => AIAssistantApi.getMoreFormal(this._text)

    this.onAction(request)
  }
  onExpand = () => {
    this._actionType = IAIActionTypeEnum.Expand
    const request = () => AIAssistantApi.getExpand(this._text)

    this.onAction(request)
  }
  onShorten = () => {
    this._actionType = IAIActionTypeEnum.Shorten
    const request = () => AIAssistantApi.getShorten(this._text)

    this.onAction(request)
  }

  onLike = () => {
    if (this._answer && this._handleOnLike) {
      this._handleOnLike(this._answer)
    }
    this.setOpen(false)
  }
  onClose = () => {
    this.setOpen(false)
  }

  setTemplate = () => {
    const dateNow = dayjs.utc().local()
    this._template = `Conversation summary from ${
      this._entities.inboxesStore.currentInbox?.name
    } ${dateNow.format(DayjsFormats.slash)}`
  }

  setLayout = (key: IAILayoutEnum) => {
    this._layout = key
  }
}
